import React from "react";
import { getComponentRefTime, toChronoUnit, toZonedDateTimeOrNow } from "utils/time-utils";
import { useCompanyTimezone } from "hooks/useTimezone";
import { ChronoUnit, Instant, LocalDate, ZoneId } from "@js-joda/core";
import DatasetTitle from "components/basic/display-titles/DatasetTitle";
import { getDweenColor } from "../charts/utils";
import { DataType, LineChartLegendProps } from "./LineChartLegendTypes";
import { DatasetLoadingConf } from "../charts/interfaces";
import "./LineChartLegend.scss";

function getDateRangeLabels(
  loadConf: DatasetLoadingConf,
  dataType: DataType,
  zone: ZoneId,
  refTime?: Instant
): string[] {
  const ref = toZonedDateTimeOrNow(zone, refTime);

  const lagUnit = toChronoUnit(loadConf.lagUnit);
  const leadUnit = toChronoUnit(loadConf.leadUnit);

  const start = ref.minus(loadConf.lag, lagUnit);
  const end = ref.plus(loadConf.lead ?? 0, leadUnit);

  const results: string[] = [];

  if (dataType === DataType.YEARS) {
    for (let year = start.year(); year <= end.year(); year++) {
      results.push(year.toString());
    }
  } else if (dataType === DataType.MONTHS) {
    let current = LocalDate.of(start.year(), start.month(), 1);
    const endMonth = LocalDate.of(end.year(), end.month(), 1);
    while (!current.isAfter(endMonth)) {
      results.push(`${current.month().name()} ${current.year()}`);
      current = current.plus(1, ChronoUnit.MONTHS);
    }
  }

  return results;
}

export default function LineChartLegend(props: LineChartLegendProps) {
  const referenceTime = getComponentRefTime(props);

  const companyTz = useCompanyTimezone();
  const isDatasets = props.dataType === DataType.DATASETS;

  const labels = isDatasets
    ? (props.datasets ?? [])
    : getDateRangeLabels(props.loadConf, props.dataType, companyTz, referenceTime);

  return (
    <div className="line-chart-legend-wrapper">
      {labels.map((label, index) => (
        <div key={label} className="line-chart-legend">
          <div className="line" style={{ backgroundColor: getDweenColor(index, props.theme) }} />
          <div className="dot" style={{ backgroundColor: getDweenColor(index, props.theme) }} />
          <span className="legend-label">{isDatasets ? <DatasetTitle identifier={label} /> : label}</span>
        </div>
      ))}
    </div>
  );
}
